import { backendApi } from "@/services/backend/api";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import {
  Position,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";

const workOrderApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    htzWorkOrderPut: build.mutation<WorkOrder, PutRequest>({
      query: (request) => ({
        url: `/htz/work-order`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzWorkOrder", id: request.id },
        "HtzWorkOrder",
      ],
    }),
    htzWorkOrderPutPosition: build.mutation<Position, PutPositionRequest>({
      query: (request) => ({
        url: `/htz/work-order/position`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzWorkOrderPosition", id: request.id },
        { type: "HtzWorkOrder", id: request.workOrderId },
        "HtzWorkOrderPosition",
      ],
    }),
    htzWorkOrderSign: build.mutation<null, SignRequest>({
      query: (request) => {
        const body = new FormData();
        body.append("id", request.id);
        body.append("signature", request.signature);
        body.append("dataFormat", request.dataFormat);
        body.append("nameOfSigner", request.nameOfSigner);
        return {
          url: `/htz/work-order/signature`,
          method: "PATCH",
          body,
          formData: true,
        };
      },
    }),
    htzWorkOrderShow: build.query<WorkOrder, ShowRequest>({
      query: (request) => ({
        url: `/htz/work-order`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("HtzWorkOrder"),
    }),
    htzWorkOrderList: build.query<Lst<WorkOrder>, ListRequest>({
      query: (request) => ({
        url: `/htz/work-order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "HtzWorkOrder"),
    }),
    htzWorkOrderListPosition: build.query<Lst<Position>, ListPositionRequest>({
      query: (request) => ({
        url: `/htz/work-order/position/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        providesList(result?.items, "HtzWorkOrderPosition"),
    }),
    htzWorkOrderDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/htz/work-order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzWorkOrder", "HtzWorkOrderPosition"],
    }),
    htzWorkOrderDeletePosition: build.mutation<null, DeletePositionRequest>({
      query: (request) => ({
        url: `/htz/work-order/position`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzWorkOrder", "HtzWorkOrderPosition"],
    }),
    htzWorkOrderPhotoDocumentationUploadImage: build.mutation<
      null,
      UploadImageRequest
    >({
      query: (request) => {
        const body = new FormData();
        body.append("id", request.id);
        body.append("positionId", request.positionId);
        body.append("photoId", request.photoId);
        body.append("fileId", request.fileId);
        body.append("file", request.file);
        return {
          url: `/htz/work-order/photo-documentation/photos`,
          method: "POST",
          body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzWorkOrderPosition", id: request.positionId },
        "HtzWorkOrderPosition",
      ],
    }),
    htzWorkOrderPhotoDocumentationShowImage: build.query<
      { objectURL: string },
      ShowImageRequest
    >({
      query: (request) => ({
        url: `/htz/work-order/photo-documentation/photos`,
        method: "GET",
        params: request,
        responseHandler: async (response) => {
          if (response.status !== 200) {
            return response.json();
          }
          return {
            objectURL: window.URL.createObjectURL(await response.blob()),
          };
        },
        // IMPORTANT. Since this query will download the Image under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    htzWorkOrderPhotoDocumentationDeleteImage: build.mutation<
      null,
      DeleteImageRequest
    >({
      query: (request) => ({
        url: `/htz/work-order/photo-documentation/photos`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzWorkOrderPosition", id: request.positionId },
        "HtzWorkOrderPosition",
        "HtzWorkOrder",
      ],
    }),
    htzWorkOrderPdfPreview: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/htz/work-order`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "HtzWorkOrder", id: request.workOrderId },
      ],
    }),
    htzWorkOrderPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      DownloadPDFRequest
    >({
      query: (request) => ({
        url: `/htz/work-order`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    htzWorkOrderSetImmutable: build.mutation<WorkOrder, SetImmutableRequest>({
      query: (request) => ({
        url: `/htz/work-order/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzWorkOrder", id: request.id },
        "HtzWorkOrder",
      ],
    }),
  }),
});

export const {
  useHtzWorkOrderPutMutation,
  useHtzWorkOrderPutPositionMutation,
  useHtzWorkOrderSignMutation,
  useHtzWorkOrderShowQuery,
  useLazyHtzWorkOrderShowQuery,
  useHtzWorkOrderListQuery,
  useHtzWorkOrderListPositionQuery,
  useLazyHtzWorkOrderListPositionQuery,
  useHtzWorkOrderDeleteMutation,
  useHtzWorkOrderDeletePositionMutation,
  useHtzWorkOrderPhotoDocumentationUploadImageMutation,
  useHtzWorkOrderPhotoDocumentationShowImageQuery,
  useHtzWorkOrderPhotoDocumentationDeleteImageMutation,
  useHtzWorkOrderPdfPreviewQuery,
  useHtzWorkOrderPdfDownloadMutation,
  useHtzWorkOrderSetImmutableMutation,
} = workOrderApi;

export type PutRequest = WorkOrder;

export type PutPositionRequest = Position;

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  state?: Filter<FulfillmentState>;
  orderBy?: OrdrBy<WorkOrderOrderBy>[];
  executingBranch?: Filter<string>;
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export type WorkOrderOrderBy =
  | "state"
  | "beginDate"
  | "endDate"
  | "teamLeader"
  | "createdAt"
  | "updatedAt"
  | "immutabilitySetAt";

export interface ListPositionRequest {
  workOrder?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface DeletePositionRequest {
  id: string;
  workOrderId: string;
}

export enum WorkOrderDocument {
  ALL = "all",
  WORK_ORDER = "workOrder",
}

export interface UploadImageRequest {
  id: string;
  positionId: string;
  photoId: string;
  fileId: string;
  file: File;
}

export interface ShowImageRequest {
  id: string;
  fileId: string;
}

export interface DeleteImageRequest {
  id: string;
  positionId: string;
  photoId: string;
  fileId: string;
}

export interface PreviewPDFRequest {
  workOrderId: string;
  document: WorkOrderDocument;
}

export interface DownloadPDFRequest {
  workOrderId: string;
  document: WorkOrderDocument;
}

export interface SetImmutableRequest {
  id: string;
  reason: string;
  setBy: string;
}
